import { queryClient } from 'utils/query';
import { createNormalizedCache } from './createNormalizedCache';

// The normalized cache keeps one version of each entity (e.g. an Issue) across
// every query that opts in, so that a change to it -- from a refetch, a
// real-time event, or a mutation -- shows everywhere it is listed. Built on
// normy (`@normy/react-query`).
//
// - Queries opt in with `normalizedQueryOptions`. Their `queryFn` returns SDK
//   data; the query's data is plain (`Normalized<T>`): no SDK class instances,
//   so check types by `discriminator` rather than `instanceof`.
// - Entities are recognized by kind (`ENTITY_CLASSES`, `PROJECTIONS` in
//   `entityKinds.ts`); everything else in the data stays as it is. Embedded
//   projections of an entity (e.g. an Alert's `Issue`) are renamed to the
//   entity's field names, and merge with it.
// - Changes not fetched from the server go through `updateEntities` -- not
//   `setQueryData`, which would update only that one query.
// - Optimistic mutations return `optimisticEntities(...)` from `onMutate`.
// - To point a field at another entity (e.g. link an Alert to an Issue), give
//   it `{ Id }` in an entity update. Such fields are declared in
//   `ENTITY_REF_FIELDS`.
// - Mutations returning entities can update them everywhere: return
//   `toNormalized(response)` from `mutationFn`, and set
//   `meta: { normalize: true }`.
// - Merges have no notion of time: the last write wins, even from a fetch that
//   started earlier.
//
// See `pages/Demo/demos/NormalizedCacheDemo.tsx` for a usage demo.

export const { normalizedQueryOptions, getEntity, updateEntities } =
    createNormalizedCache(queryClient);

export {
    type EntityLink,
    type EntityUpdate,
    type NormalizedQueryOptions,
    type OptimisticEntitiesContext,
    optimisticEntities,
} from './createNormalizedCache';
export {
    type EntityKind,
    type EntityKinds,
    type Normalized,
    type NormalizedAlertIssueChild,
} from './entityKinds';
export { toNormalized } from './toNormalized';
